<template>
    <CompassPanel :value="showPanel" :title="panelTitle" @beforeClose="closePanel">
        <template #default>
            <div v-if="effectiveTemplate && currentStep" class="automation-wizard-panel__content">
                <AutomationWizardOverviewContainer
                    v-if="currentStep.type === 'overview'"
                    :template="effectiveTemplate"
                    :isEditMode="isEditMode"
                />
                <AutomationWizardForm
                    v-else
                    v-model="formData"
                    :currentStep="currentStep"
                    @field-change="clearFieldError"
                />
            </div>
        </template>
        <template #footer>
            <AutomationWizardPanelFooter
                :allSteps="allSteps"
                :currentStepIndex="currentStepIndex"
                :isLoading="isLoadingActivate"
                :isEditMode="isEditMode"
                @activate-automation="handleActivateAutomation"
                @previous-step="goToPreviousStep"
                @next-step="handleNextStep"
            />
        </template>
    </CompassPanel>
</template>

<script>
import AutomationWizardOverviewContainer from 'vcomponents/automation/templates/Wizard/Overview/AutomationWizardOverviewContainer.vue';
import AutomationWizardForm from 'vcomponents/automation/templates/Wizard/Form/AutomationWizardForm.vue';
import AutomationWizardPanelFooter from 'vcomponents/automation/templates/Wizard/AutomationWizardPanelFooter.vue';

import {
    useAutomationTemplate,
    enableAutomation,
    updateAutomation,
} from 'api/automation/automationService.js';

import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

export default {
    components: {
        AutomationWizardOverviewContainer,
        AutomationWizardForm,
        AutomationWizardPanelFooter,
    },
    props: {
        template: {
            type: Object,
            default: null,
        },
        automation: {
            type: Object,
            default: null,
        },
        isEditMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            translations: {
                title: this.$t.__('automation.AutomationWizardPanel.title'),
                titleEdit: this.$t.__('automation.AutomationWizardPanel.title_edit'),
            },
            isLoadingActivate: false,
            formData: {},
            currentStepIndex: 0,
            showPanel: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
        allSteps() {
            return this.effectiveTemplate?.metadata?.wizard_steps || [];
        },
        currentStep() {
            return this.allSteps[this.currentStepIndex] || null;
        },
        panelTitle() {
            return this.isEditMode ? this.translations.titleEdit : this.translations.title;
        },
        effectiveTemplate() {
            if (this.isEditMode && this.automation?.metadata) {
                return {
                    id: this.automation.id,
                    metadata: this.automation.metadata,
                    title:
                        this.automation.version?.displayName ||
                        this.automation.metadata?.template.title,
                    description: this.automation.metadata?.template?.description || '',
                    pieces: this.extractPiecesFromAutomation(),
                };
            }
            return this.template;
        },
    },
    watch: {
        $route() {
            this.closePanel();
        },
        template() {
            this.resetPanel();
        },
        automation() {
            this.resetPanel();
        },
        showPanel(newValue) {
            if (newValue) {
                this.resetPanel();
            }
        },
    },
    created() {
        this.initializeFormData();
    },
    mounted() {
        this.showPanel = true;
    },
    methods: {
        resetPanel() {
            this.currentStepIndex = 0;
            this.clearAllErrors();
            this.initializeFormData();
        },
        closePanel() {
            this.clearAllErrors();
            this.showPanel = false;
            this.$nextTick(() => this.$emit('close'));
        },
        activateAutomation(id) {
            return enableAutomation(id)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        close: true,
                        text: this.$t.__('automation.common.success.start_automation'),
                    });
                })
                .catch((error) => {
                    let textToast = this.$t.__('automation.common.error.stop_automation');
                    if (error.response.status === 409) {
                        textToast = this.$t.__(
                            'automation.automationListingTableRow.toast.error.wait_minute',
                        );
                    }
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: textToast,
                    });
                    throw error;
                });
        },
        async handleActivateAutomation() {
            this.isLoadingActivate = true;
            try {
                if (this.isEditMode) {
                    await this.updateExistingAutomation();
                } else {
                    await this.createNewAutomation();
                }
                this.navigateToListing();
            } catch (error) {
                this.$pushToast({
                    type: 'error',
                    close: true,
                    text: this.$t.__('common.labels.toast.error'),
                });
                throw error;
            } finally {
                this.isLoadingActivate = false;
            }
        },
        async createNewAutomation() {
            const preparedData = this.prepareFormDataForSubmission();
            const result = await useAutomationTemplate(this.effectiveTemplate.id, preparedData);
            await this.activateAutomation(result.data.id);
        },
        async updateExistingAutomation() {
            const preparedData = this.prepareFormDataForSubmission();
            await updateAutomation(this.automation.id, preparedData);
            this.$pushToast({
                type: 'success',
                close: true,
                text: this.$t.__('automation.common.success.automation_updated'),
            });
        },
        navigateToListing() {
            this.closePanel();
            if (this.isEditMode) {
                EventBus.emit('automation-updated');
            } else {
                this.$router.push({ name: 'automations.listing' });
            }
        },
        goToPreviousStep() {
            if (this.currentStepIndex > 0) {
                this.currentStepIndex--;
            }
        },
        goToNextStep() {
            if (this.currentStepIndex < this.allSteps.length - 1) {
                this.currentStepIndex++;
            }
        },
        handleNextStep() {
            this.isLoadingActivate = true;
            const isLastStep = this.currentStepIndex === this.allSteps.length - 1;
            if (isLastStep) {
                if (this.validateCurrentStep()) {
                    this.handleActivateAutomation();
                } else {
                    this.isLoadingActivate = false;
                }
            } else if (this.validateCurrentStep()) {
                this.isLoadingActivate = false;
                this.goToNextStep();
            } else {
                this.isLoadingActivate = false;
            }
        },
        validateCurrentStep() {
            if (!this.currentStep || !this.currentStep.fields) {
                return true;
            }
            for (const field of this.currentStep.fields) {
                this.$delete(field, 'currentError');
            }
            const requiredFields = this.currentStep.fields.filter(
                (field) => field.required && field.type !== 'hidden',
            );
            let hasErrors = false;
            for (const field of requiredFields) {
                const fieldValue = this.formData[field.id];
                const isEmpty = this.isFieldValueEmpty(fieldValue);

                if (isEmpty) {
                    const errorMessage = field.props?.formItem?.error;
                    this.$set(field, 'currentError', errorMessage);
                    hasErrors = true;
                }
            }
            return !hasErrors;
        },
        isFieldValueEmpty(value) {
            return !value || (Array.isArray(value) && value.length === 0);
        },
        clearFieldError(fieldName) {
            if (!this.currentStep || !this.currentStep.fields) {
                return;
            }
            const field = this.currentStep.fields.find((f) => f.id === fieldName);
            if (field && field.currentError) {
                this.$delete(field, 'currentError');
            }
        },
        clearAllErrors() {
            if (!this.effectiveTemplate?.metadata?.wizard_steps) {
                return;
            }
            for (const step of this.effectiveTemplate.metadata.wizard_steps) {
                if (step.fields) {
                    for (const field of step.fields) {
                        if (field.currentError) {
                            this.$delete(field, 'currentError');
                        }
                    }
                }
            }
        },
        prepareFormDataForSubmission() {
            const templateData = JSON.parse(JSON.stringify(this.effectiveTemplate.metadata));
            if (this.template) {
                templateData.template = {};
                templateData.template.id = this.effectiveTemplate.id;
                templateData.template.title = this.effectiveTemplate.title;
                templateData.template.description = this.effectiveTemplate.description;
            }

            if (templateData.wizard_steps) {
                for (const step of templateData.wizard_steps) {
                    if (step.fields) {
                        for (const field of step.fields) {
                            this.updateFieldWithFormData(field);
                        }
                    }
                }
            }
            return {
                metadata: templateData,
            };
        },
        updateFieldWithFormData(field) {
            const fieldValue = this.formData[field.id];
            if (fieldValue !== undefined && fieldValue !== '') {
                field.value = fieldValue;
                this.synchronizeFieldProps(field, fieldValue);
            }
        },
        synchronizeFieldProps(field, fieldValue) {
            switch (field.type) {
                case 'select':
                case 'pipeline_step_opp':
                    this.updateSelectFieldProps(field, fieldValue);
                    break;
                case 'date_time':
                    this.updateDateTimeFieldProps(field, fieldValue);
                    break;
                case 'date':
                case 'time':
                case 'text':
                case 'textarea':
                case 'amount':
                    this.updateInputFieldProps(field, fieldValue);
                    break;
                case 'priority_task':
                    this.updatePriorityFieldProps(field, fieldValue);
                    break;
                default:
                    break;
            }
        },
        updateSelectFieldProps(field, fieldValue) {
            if (field.props?.select) {
                field.props.select.value = fieldValue;
            }
        },
        updateDateTimeFieldProps(field, fieldValue) {
            const dayValue = this.extractValuePart(fieldValue, 'day');
            const hourValue = this.extractValuePart(fieldValue, 'hour');
            if (field.props?.input && dayValue) {
                field.props.input.value = dayValue;
            }
            if (field.props?.timePicker && hourValue) {
                field.props.timePicker.value = hourValue;
            }
        },
        extractValuePart(value, unit) {
            if (!value || typeof value !== 'string') {
                return '';
            }
            const match = value.match(new RegExp(`\\+\\s*(\\d+(?:\\.\\d+)?)\\s*${unit}`));
            return match ? match[0] : '';
        },
        updateInputFieldProps(field, fieldValue) {
            if (field.props?.input) {
                field.props.input.value = fieldValue;
            }
        },
        updatePriorityFieldProps(field, fieldValue) {
            if (field.props?.iconButton) {
                field.props.iconButton.value = fieldValue;
            }
        },
        extractPiecesFromAutomation() {
            if (this.automation?.pieces && Array.isArray(this.automation.pieces)) {
                return this.automation.pieces;
            }
            const pieces = new Set();
            if (this.automation?.version?.trigger?.settings?.pieceName) {
                pieces.add(this.automation.version.trigger.settings.pieceName);
            }
            if (this.automation?.metadata?.wizard_steps) {
                for (const step of this.automation.metadata.wizard_steps) {
                    if (step.stepName && step.stepName.includes('@activepieces/piece-')) {
                        const match = step.stepName.match(/@activepieces\/piece-[^/]+/);
                        if (match) {
                            pieces.add(match[0]);
                        }
                    }
                }
            }
            return Array.from(pieces);
        },
        initializeFormData() {
            if (!this.effectiveTemplate?.metadata?.wizard_steps) {
                this.formData = {};
                return;
            }
            const newFormData = {};
            for (const step of this.effectiveTemplate.metadata.wizard_steps) {
                if (step.fields) {
                    for (const field of step.fields) {
                        let initialValue = '';
                        if (field.props?.select?.value !== undefined) {
                            initialValue = field.props.select.value;
                        } else if (field.props?.iconButton?.value !== undefined) {
                            initialValue = field.props.iconButton.value;
                        } else if (field.props?.input?.value !== undefined) {
                            initialValue = field.props.input.value;
                        }
                        newFormData[field.id] = initialValue;
                    }
                }
            }

            this.formData = newFormData;
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-wizard-panel {
    &__content {
        display: flex;
        flex-direction: column;
    }
}
</style>
